import { computed, signal, Signal } from '@angular/core';
import {
  catchError,
  combineLatestWith,
  defer,
  finalize,
  mergeWith,
  Observable,
  pipe,
  skip,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { toSignal, ToSignalOptions } from '@angular/core/rxjs-interop';

export type StatefulSignal<T, U extends any[] = any[]> = Signal<T> & {
  /**
   * Set a new value for the signal.
   */
  set: (value: T) => void /**
   * Set a new value for the signal based on the previous value
   */;
  update: (updateFn: (value: T) => T) => void;
  /**
   * Produces another observable from the factory and runs it with the given state.
   * If omitted, uses the latest state from the state observables.
   */
  next: { (...args: U): void; (): void };
  /**
   * Indicates whether an observable is currently running.
   */
  busy: Signal<boolean>;
  /**
   * Stores the error associated to the last produced observable, if any.
   */
  error: Signal<unknown>;
  /**
   * Refreshes the signal and returns an observable that emits the refreshed resource.
   */
  refresh: () => Observable<T>;
};

export type StatefulSignalOptions<T> = {
  /**
   * State observables can be used to pass reactive state to the factory.
   */
  state?: ObservableTuple<T>;
  /**
   * When set to true emissions that lead to errors are removed from the stream
   * and errors ignored.
   */
  ignoreErrors?: boolean | Parameters<typeof catchError>[0];
  /**
   * When set to false the signal will not emit until a first call to `.next` is made
   */
  initialEmit?: boolean;
  /**
   * When set to true the refresh observable will skip the first emitted value.
   * Useful for when the observable factory replays its last value
   */
  skipFirstRefresh?: boolean;
};

/**
 * A stateful signal runs an observable produced by the observable factory each
 * time its `.next` method is called, or when one of the state observables
 * changes. The observable factory is handed the latest value of each of the
 * state observables. While the produced observable is running the `.busy` signal
 * is true. If a produced observable leads to an error it is stored in the `.error` signal
 *
 * @param obsFactory Must return an observable to be executed at each trigger/state change
 * @param options
 */
export function toStatefulSignal<T extends any[], U>(
  obsFactory: (...x: T) => Observable<U>,
  options: StatefulSignalOptions<T> &
    ToSignalOptions & {
      initialValue?: U;
    } = {},
): StatefulSignal<U, T> {
  // Used to trigger/refresh the signal producing a new state.
  const stateTrigger = new Subject<void | T>();
  // Used to trigger a direct emission of a new value.
  const setter = new Subject<U>();
  // Used to indicate busy status
  const busy = signal(false);
  // Used to store latest error object
  const error = signal<unknown>(undefined);
  // Determine how to handle errors:
  // * ignoreErrors not set or falsy: rethrow everything
  // * ignoreErrors is true: catch and ignore all errors
  // * ignoreErrors is a function: use it to catch errrors.
  const errorHandler: Parameters<typeof catchError>[0] = !options.ignoreErrors
    ? (err) => {
        throw err;
      }
    : typeof options.ignoreErrors === 'boolean'
      ? (err, caught) => caught
      : options.ignoreErrors;

  let lastValue = options.initialValue;
  let observable: Observable<U>;

  const statefulSignal = toSignal(
    // Formatting instability between husky and prettier
    // prettier-ignore
    (observable = (options.initialEmit ?? true
      ? // Prime the trigger so that the pipe starts immediately
        stateTrigger.pipe(startWith(void 0))
      : // or wait for the trigger if `initialEmit` is false
        stateTrigger
    ).pipe(
      // Combine the trigger with every state observable the signal depends on.
      combineLatestWith(...(options.state ?? ([] as ObservableTuple<T>))),
      switchMap(([trigger, ...state]) => {
        // Reset state when producing new observable
        error.set(undefined);
        busy.set(true);
        // Are trigger values given? If so, replace state.
        if (trigger) {
          state = trigger;
        }
        // Call the observable factory for each trigger or state change
        return obsFactory(...state).pipe(finalize(() => busy.set(false)));
      }),
      mergeWith(setter),
      tap((value) => lastValue = value),
      // Catch error states
      catchError((err, caught) => {
        // Store the error object
        error.set(err);
        // Handle the error
        return errorHandler(err, caught);
      }),
    )),
    options as any,
  ) as StatefulSignal<U, T>;

  // Assemble the stateful signal.

  // 1. next() manually retriggers the observable factory
  // 2. next(...state) retriggers with specific state values
  statefulSignal.next = ((...state: T) =>
    state.length ? stateTrigger.next(state) : stateTrigger.next()) as {
    (): void;
    (...state: T): void;
  };
  // 3. set(value) sets the next value
  statefulSignal.set = (value: U) => setter.next(value);
  statefulSignal.update = (updateFn: (value: U) => U) =>
    setter.next(updateFn(lastValue!));
  // 4. busy reflects signal busy state
  statefulSignal.busy = computed(() => busy());
  // 5. error reflects signal error state
  statefulSignal.error = computed(() => error());
  // 6. refresh calls next and waits for resource as observable
  statefulSignal.refresh = () =>
    defer(() => {
      const obs = observable.pipe(
        // Some underlying observables are replaying their value immediately,
        // and we want to ignore this when we are refreshing
        options.skipFirstRefresh ? pipe(skip(1)) : pipe(),
        take(1),
      );
      statefulSignal.next();
      return obs;
    });

  return statefulSignal;
}

// Transform [A, B, C] to [Observable<A>, Observable<B>, Observable<C>]
type ObservableTuple<T> = { [K in keyof T]: Observable<T[K]> };
